// Reads codes the way the API does, so the upload preview shows what will be added. The API takes
// each row's first CSV column, trimmed, skipping blank rows and, if asked, the first row as a
// header.

// Splits one line of CSV into its cells. A quoted cell can hold commas and doubled quotes, but not
// a line break; a code with a line break in it isn't a code anyone could type.
const splitCsvLine = (line: string) => {
  const cells: string[] = [];
  let cell = "";
  let inQuotes = false;
  for (let i = 0; i < line.length; i++) {
    const char = line[i];
    if (inQuotes) {
      if (char === '"' && line[i + 1] === '"') {
        cell += '"';
        i++;
      } else if (char === '"') {
        inQuotes = false;
      } else {
        cell += char;
      }
    } else if (char === '"') {
      inQuotes = true;
    } else if (char === ",") {
      cells.push(cell);
      cell = "";
    } else {
      cell += char;
    }
  }
  cells.push(cell);
  return cells;
};

export type ParsedCodes = {
  codes: string[];
  // The skipped first row's first cell, when `hasHeader` is set.
  header: string | null;
};

export const parseCodes = (text: string, hasHeader: boolean): ParsedCodes => {
  const rows = text
    .split(/\r\n|\r|\n/)
    .map(splitCsvLine)
    .filter((cells) => cells.some((cell) => cell.trim()));
  const header = hasHeader && rows.length ? rows.shift()![0].trim() : null;
  return {
    codes: rows.map((cells) => cells[0].trim()).filter(Boolean),
    header,
  };
};

// Decodes a file as the API will: UTF-16 with a byte order mark, else UTF-8, else Windows-1252,
// which Excel on Windows often saves CSVs in. Returns null for a file that isn't text, such as a
// spreadsheet.
export const readCodeFile = async (file: Blob): Promise<string | null> => {
  const bytes = new Uint8Array(await file.arrayBuffer());
  if (bytes[0] === 0xff && bytes[1] === 0xfe) {
    return new TextDecoder("utf-16le").decode(bytes);
  }
  if (bytes[0] === 0xfe && bytes[1] === 0xff) {
    return new TextDecoder("utf-16be").decode(bytes);
  }
  if (bytes.includes(0)) {
    return null;
  }
  try {
    return new TextDecoder("utf-8", { fatal: true }).decode(bytes);
  } catch {
    return new TextDecoder("windows-1252").decode(bytes);
  }
};
